import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { NavArrowLeft, Copy, Check } from 'iconoir-react';
import '../admin.css';

interface ApiDoc {
  version: string;
  base_url: string;
  authentication: {
    type: string;
    header: string;
    description: string;
  };
  endpoints: Record<string, any[]>;
  rate_limiting: {
    limit: string;
    headers: Record<string, string>;
  };
}

const ApiDocumentation: React.FC = () => {
  const navigate = useNavigate();
  const [doc, setDoc] = useState<ApiDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('introduction');

  useEffect(() => {
    loadDocumentation();
  }, []);

  useEffect(() => {
    // Observer pour mettre à jour la section active lors du scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    document.querySelectorAll('.doc-section').forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, [doc]);

  const loadDocumentation = async () => {
    try {
      const response = await api.get('/api-tokens/documentation');
      setDoc(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const CodeBlock: React.FC<{ code: string; language: string; id: string }> = ({ code, language, id }) => (
    <div className="code-block">
      <div className="code-header">
        <span className="code-language">{language}</span>
        <button
          className="btn-copy"
          onClick={() => copyToClipboard(code, id)}
        >
          {copiedCode === id ? (
            <>
              <Check width={16} height={16} strokeWidth={2} />
              Copié
            </>
          ) : (
            <>
              <Copy width={16} height={16} strokeWidth={2} />
              Copier
            </>
          )}
        </button>
      </div>
      <pre><code>{code}</code></pre>
    </div>
  );

  if (loading) return <div>Chargement...</div>;
  if (!doc) return <div>Erreur lors du chargement de la documentation</div>;

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="api-documentation">
      <div className="doc-header">
        <div>
          <h1>Documentation API Ventilio</h1>
          <p>Version {doc.version} - Intégrez Ventilio avec vos outils préférés</p>
        </div>
        <button onClick={() => navigate('/')} className="btn-back">
          <NavArrowLeft width={20} height={20} />
          Retour
        </button>
      </div>

      <div className="doc-layout">
        {/* Sommaire */}
        <aside className="doc-sidebar">
          <nav className="doc-nav">
            <h3>Sommaire</h3>
            <ul>
              <li className={activeSection === 'introduction' ? 'active' : ''}>
                <a onClick={() => scrollToSection('introduction')}>Introduction</a>
              </li>
              <li className={activeSection === 'authentication' ? 'active' : ''}>
                <a onClick={() => scrollToSection('authentication')}>Authentification</a>
              </li>
              <li className={activeSection === 'rate-limiting' ? 'active' : ''}>
                <a onClick={() => scrollToSection('rate-limiting')}>Limitation de débit</a>
              </li>
              <li className={activeSection === 'boards' ? 'active' : ''}>
                <a onClick={() => scrollToSection('boards')}>Tableaux</a>
              </li>
              <li className={activeSection === 'cards' ? 'active' : ''}>
                <a onClick={() => scrollToSection('cards')}>Cartes</a>
              </li>
              <li className={activeSection === 'columns' ? 'active' : ''}>
                <a onClick={() => scrollToSection('columns')}>Colonnes</a>
              </li>
              <li className={activeSection === 'labels' ? 'active' : ''}>
                <a onClick={() => scrollToSection('labels')}>Étiquettes</a>
              </li>
              <li className={activeSection === 'comments' ? 'active' : ''}>
                <a onClick={() => scrollToSection('comments')}>Commentaires</a>
              </li>
              <li className={activeSection === 'errors' ? 'active' : ''}>
                <a onClick={() => scrollToSection('errors')}>Codes d'erreur</a>
              </li>
              <li className={activeSection === 'support' ? 'active' : ''}>
                <a onClick={() => scrollToSection('support')}>Support</a>
              </li>
            </ul>
          </nav>
        </aside>

        <div className="doc-content">
        {/* Introduction */}
        <section id="introduction" className="doc-section">
          <h2>Introduction</h2>
          <p>
            L'API Ventilio vous permet d'intégrer vos tableaux Kanban avec vos outils tiers
            (Zapier, Make, webhooks personnalisés, etc.). Toutes les requêtes doivent être
            authentifiées avec un token API.
          </p>
          <div className="doc-info-box">
            <strong>URL de base :</strong> <code>{doc.base_url}</code>
          </div>
        </section>

        {/* Authentification */}
        <section id="authentication" className="doc-section">
          <h2>Authentification</h2>
          <p>{doc.authentication.description}</p>
          
          <h3>Format de l'en-tête</h3>
          <CodeBlock
            id="auth-header"
            language="HTTP"
            code={doc.authentication.header}
          />

          <h3>Exemple avec cURL</h3>
          <CodeBlock
            id="curl-example"
            language="bash"
            code={`curl -X GET "${doc.base_url}/boards" \\
  -H "${doc.authentication.header}"`}
          />

          <h3>Exemple avec JavaScript (fetch)</h3>
          <CodeBlock
            id="js-example"
            language="javascript"
            code={`fetch('${doc.base_url}/boards', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer YOUR_API_TOKEN',
    'Content-Type': 'application/json'
  }
})
.then(response => response.json())
.then(data => console.log(data));`}
          />

          <h3>Exemple avec Python (requests)</h3>
          <CodeBlock
            id="python-example"
            language="python"
            code={`import requests

headers = {
    'Authorization': 'Bearer YOUR_API_TOKEN',
    'Content-Type': 'application/json'
}

response = requests.get('${doc.base_url}/boards', headers=headers)
data = response.json()
print(data)`}
          />
        </section>

        {/* Rate Limiting */}
        <section id="rate-limiting" className="doc-section">
          <h2>Limitation de débit</h2>
          <p>
            <strong>{doc.rate_limiting.limit}</strong>
          </p>
          <p>Les en-têtes de réponse suivants indiquent votre utilisation :</p>
          <ul>
            {Object.entries(doc.rate_limiting.headers).map(([header, description]) => (
              <li key={header}>
                <code>{header}</code> : {description}
              </li>
            ))}
          </ul>
        </section>

        {/* Endpoints - Tableaux */}
        <section id="boards" className="doc-section">
          <h2>Endpoints - Tableaux</h2>
          {doc.endpoints.boards?.map((endpoint, index) => (
            <div key={index} className="endpoint-card">
              <div className="endpoint-header">
                <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                  {endpoint.method}
                </span>
                <code className="endpoint-path">{endpoint.endpoint}</code>
              </div>
              <div className="endpoint-body">
                <p><strong>Description :</strong> {endpoint.description}</p>
                <p><strong>Scope requis :</strong> <code>{endpoint.scope}</code></p>
                
                {endpoint.parameters && (
                  <>
                    <p><strong>Paramètres :</strong></p>
                    <ul>
                      {Object.entries(endpoint.parameters).map(([param, desc]) => (
                        <li key={param}>
                          <code>{param}</code> : {desc as string}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {endpoint.response && (
                  <>
                    <p><strong>Exemple de réponse :</strong></p>
                    <CodeBlock
                      id={`response-${index}`}
                      language="json"
                      code={JSON.stringify(endpoint.response, null, 2)}
                    />
                  </>
                )}

                {endpoint.method === 'POST' && endpoint.parameters && (
                  <>
                    <p><strong>Exemple de requête :</strong></p>
                    <CodeBlock
                      id={`request-${index}`}
                      language="bash"
                      code={`curl -X POST "${doc.base_url}${endpoint.endpoint}" \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(
    Object.keys(endpoint.parameters).reduce((acc, key) => {
      acc[key] = key === 'title' ? 'Mon nouveau tableau' : 'valeur';
      return acc;
    }, {} as Record<string, string>),
    null,
    2
  )}'`}
                    />
                  </>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* Endpoints - Cartes */}
        <section id="cards" className="doc-section">
          <h2>Endpoints - Cartes</h2>
          {doc.endpoints.cards?.map((endpoint, index) => (
            <div key={index} className="endpoint-card">
              <div className="endpoint-header">
                <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                  {endpoint.method}
                </span>
                <code className="endpoint-path">{endpoint.endpoint}</code>
              </div>
              <div className="endpoint-body">
                <p><strong>Description :</strong> {endpoint.description}</p>
                <p><strong>Scope requis :</strong> <code>{endpoint.scope}</code></p>
                
                {endpoint.parameters && (
                  <>
                    <p><strong>Paramètres :</strong></p>
                    <ul>
                      {Object.entries(endpoint.parameters).map(([param, desc]) => (
                        <li key={param}>
                          <code>{param}</code> : {desc as string}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* Endpoints - Colonnes */}
        <section id="columns" className="doc-section">
          <h2>Endpoints - Colonnes</h2>
          {doc.endpoints.columns?.map((endpoint, index) => (
            <div key={index} className="endpoint-card">
              <div className="endpoint-header">
                <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                  {endpoint.method}
                </span>
                <code className="endpoint-path">{endpoint.endpoint}</code>
              </div>
              <div className="endpoint-body">
                <p><strong>Description :</strong> {endpoint.description}</p>
                <p><strong>Scope requis :</strong> <code>{endpoint.scope}</code></p>
                
                {endpoint.parameters && (
                  <>
                    <p><strong>Paramètres :</strong></p>
                    <ul>
                      {Object.entries(endpoint.parameters).map(([param, desc]) => (
                        <li key={param}>
                          <code>{param}</code> : {desc as string}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* Endpoints - Étiquettes */}
        <section id="labels" className="doc-section">
          <h2>Endpoints - Étiquettes</h2>
          {doc.endpoints.labels?.map((endpoint, index) => (
            <div key={index} className="endpoint-card">
              <div className="endpoint-header">
                <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                  {endpoint.method}
                </span>
                <code className="endpoint-path">{endpoint.endpoint}</code>
              </div>
              <div className="endpoint-body">
                <p><strong>Description :</strong> {endpoint.description}</p>
                <p><strong>Scope requis :</strong> <code>{endpoint.scope}</code></p>
              </div>
            </div>
          ))}
        </section>

        {/* Endpoints - Commentaires */}
        <section id="comments" className="doc-section">
          <h2>Endpoints - Commentaires</h2>
          {doc.endpoints.comments?.map((endpoint, index) => (
            <div key={index} className="endpoint-card">
              <div className="endpoint-header">
                <span className={`method-badge method-${endpoint.method.toLowerCase()}`}>
                  {endpoint.method}
                </span>
                <code className="endpoint-path">{endpoint.endpoint}</code>
              </div>
              <div className="endpoint-body">
                <p><strong>Description :</strong> {endpoint.description}</p>
                <p><strong>Scope requis :</strong> <code>{endpoint.scope}</code></p>
                
                {endpoint.parameters && (
                  <>
                    <p><strong>Paramètres :</strong></p>
                    <ul>
                      {Object.entries(endpoint.parameters).map(([param, desc]) => (
                        <li key={param}>
                          <code>{param}</code> : {desc as string}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>

        {/* Codes d'erreur */}
        <section id="errors" className="doc-section">
          <h2>Codes d'erreur</h2>
          <table className="error-codes-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>200</code></td>
                <td>Succès</td>
              </tr>
              <tr>
                <td><code>201</code></td>
                <td>Ressource créée avec succès</td>
              </tr>
              <tr>
                <td><code>400</code></td>
                <td>Requête invalide</td>
              </tr>
              <tr>
                <td><code>401</code></td>
                <td>Non authentifié (token manquant ou invalide)</td>
              </tr>
              <tr>
                <td><code>403</code></td>
                <td>Permission insuffisante (scope manquant)</td>
              </tr>
              <tr>
                <td><code>404</code></td>
                <td>Ressource non trouvée</td>
              </tr>
              <tr>
                <td><code>429</code></td>
                <td>Trop de requêtes (rate limit dépassé)</td>
              </tr>
              <tr>
                <td><code>500</code></td>
                <td>Erreur serveur</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Support */}
        <section id="support" className="doc-section">
          <h2>Support</h2>
          <p>
            Pour toute question ou problème concernant l'API, n'hésitez pas à nous contacter
            via le système de support intégré dans votre tableau de bord.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => navigate('/profile')}
          >
            Gérer mes tokens API
          </button>
        </section>
      </div>
      </div>
    </div>
  );
};

export default ApiDocumentation;
